import { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import * as Dialog from "@radix-ui/react-dialog";
import CloseButton from "@hawkins/assets/lib/icons/iconX";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { animationCurves } from "../../utils/animationCurves";
import { Button } from "../shared/core/Button";

type FullVideoPlayerProps = {
  isOpen: boolean;
  videoUrl: string;
  setIsOpen: (i: boolean) => void;
};

const videoPreviewVariants = {
  initial: {
    width: "50%",
    height: "50%",
    opacity: 0,
    top: "25%",
    left: "25%",
  },
  animate: {
    top: "12.5%",
    left: "12.5%",
    opacity: 1,
    width: "75%",
    height: "75%",
  },
  exit: {
    width: "50%",
    height: "50%",
    opacity: 0,
    top: "25%",
    left: "25%",
  },
};

const FullVideoPlayer = ({
  isOpen,
  videoUrl,
  setIsOpen,
}: FullVideoPlayerProps) => {
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    document.addEventListener("fullscreenchange", () => {
      setIsOpen(!!document.fullscreenElement);
    });
  }, [setIsOpen]);

  useEffect(() => {
    if (isOpen) {
      mobileVideoRef?.current?.requestFullscreen();
    }
  }, [isOpen]);

  if (!isDesktop) {
    if (isOpen) {
      return (
        <div className="absolute top-0 left-0 w-0 h-0 z-50">
          <video
            ref={mobileVideoRef}
            autoPlay
            controls
            src={videoUrl}
            className="inset-0 size-full object-contain origin-center"
          />
        </div>
      );
    }
    return null;
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <AnimatePresence>
        {isOpen && (
          <Dialog.Portal forceMount>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Dialog.Overlay className="fixed bg-brand-black/80 inset-0 z-50" />
            </motion.div>
            <Dialog.Content>
              <motion.div
                className="fixed z-50 bg-brand-black w-3/4 h-3/4 rounded-lg"
                initial="initial"
                animate="animate"
                exit="exit"
                variants={videoPreviewVariants}
                transition={animationCurves.easeOut}
              >
                <Dialog.Title />
                <div className="w-full h-full rounded-lg">
                  <video
                    autoPlay
                    controls
                    src={videoUrl}
                    className="inset-0 size-full object-cover origin-center z-50 rounded-lg"
                  />
                </div>
                <motion.div
                  className="fixed right-[calc(12.5%-45px)] top-[12.5%] z-50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Dialog.Close asChild>
                    <Button size="small">
                      <CloseButton size="small" />
                    </Button>
                  </Dialog.Close>
                </motion.div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
};

export default FullVideoPlayer;
